// client/src/intro-exit/NoGame.jsx

/**
 * Terminal screen for participants who never completed a game: either they were
 * never matched into one, or the game/batch was cancelled from the admin panel
 * while they were in it.
 *
 * These participants are owed the base wage for their time but have no survey to
 * answer and no bonus to report, so this page skips the exit survey entirely and
 * goes straight to the completion code. The code is the same one the normal exit
 * shows -- it is imported rather than repeated so the two screens cannot drift.
 */

import React, { useEffect } from "react";
import { PROLIFIC_CODE } from "./Exit";

export function NoGame({ player, game }) {
  // The server writes basePay onto every player as soon as they are created, reading
  // it from the batch's own treatment configuration, so it is set here even for
  // someone who never got a game. The treatment is preferred when there is one (a
  // cancelled game), and there is deliberately no hard-coded fallback: a constant
  // kept next to the real value drifts from it, which is how this screen came to
  // advertise $2.00 while the batch was running at $2.50. If neither source has a
  // number, the amount is omitted rather than invented.
  const treatmentBasePay = game?.get("treatment")?.basePay;
  const raw = treatmentBasePay !== undefined ? treatmentBasePay : player?.get("basePay");
  const basePay = raw === undefined || raw === null ? null : parseFloat(raw);

  // Mark these participants in the datastore so they are separable from those who
  // played through. `ended` carries Empirica's own reason ("no more games",
  // "game terminated", "game failed"), which distinguishes never-matched from
  // cancelled-mid-game without needing a second flag.
  //
  // Guarded on the current value rather than written unconditionally: `player` is
  // not a stable identity across re-renders, so an unguarded set here would write
  // on every render and spin.
  useEffect(() => {
    if (!player) return;
    if (player.get("exitStatus") !== "no_game") {
      player.set("exitStatus", "no_game");
    }
    const reason = player.get("ended") ?? "unknown";
    if (player.get("endedReason") !== reason) {
      player.set("endedReason", reason);
    }
  }, [player]);

  return (
    <div className="min-h-screen bg-gray-50/50 py-8 px-4 sm:px-6 lg:px-8 font-sans antialiased text-gray-900 w-full flex items-center justify-center">
      <div className="max-w-[46.2rem] w-full bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden">

        <div className="bg-gray-900 px-6 py-4 text-white w-full">
          <h1 className="app-page-title">Loan Risk Assessment Experiment</h1>
          <p className="app-page-subtitle text-gray-400 mt-0.5">Your session has ended.</p>
        </div>

        <div className="p-8 sm:p-10 space-y-6">

          <div className="border-b pb-3">
            <h2 className="app-survey-title text-gray-900 text-center">
              Sorry, we could not match you to a game
            </h2>
            <p className="app-survey-note text-gray-500 text-center">
              Please come back again later.
            </p>
          </div>

          <div className="flex justify-center">
            <div className="text-center px-8 py-4 bg-gray-50 border border-gray-200 rounded-xl">
              <span className="app-micro-label text-gray-400 block">Base Wage</span>
              <strong
                className={`app-metric-value text-gray-700 mt-0.5 block ${
                  basePay === null || Number.isNaN(basePay) ? "" : "font-mono"
                }`}
              >
                {basePay === null || Number.isNaN(basePay)
                  ? "Paid in full"
                  : `$${basePay.toFixed(2)}`}
              </strong>
            </div>
          </div>

          <div className="border-2 border-dashed border-emerald-300 rounded-2xl p-8 bg-emerald-50/30 shadow-2xs space-y-3 w-full text-center">
            <span className="app-micro-label text-gray-400 block">Your Prolific Completion Token</span>
            <strong className="text-3xl font-mono font-black text-gray-900 tracking-wider select-all block py-1">
              {PROLIFIC_CODE}
            </strong>
            <p className="app-body text-gray-600 max-w-sm mx-auto pt-2">
              Please copy this code and paste it back into Prolific to validate your submission. You can now safely close this window.
            </p>
          </div>

        </div>
      </div>
    </div>
  );
}
